import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { camelCaseKeys } from "src/utility/utilityFunctions";

import { itemRollupsKeyFactory } from "@features/purchaseOrders";
import { OrderVariant } from "@models";
import {
  TrackingResponse,
  TrackingResponseError,
} from "@models/TrackingResponse";
import client from "@services/api";
import { QueryOptions, buildPaginatedQuery } from "@utils/reactQuery";

export const orderVariantsKeyFactory = createQueryKeys("order-variants", {
  list: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client
        .get<OrderVariant[]>("order-variants", { params })
        .then((res) => res.data),
  }),
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () => client.get<OrderVariant[]>("order-variants", { params }),
  }),
  track: (orderVariantId: string) => ({
    queryKey: [orderVariantId],
    queryFn: () =>
      client
        .get(`order-variants/${orderVariantId}/track`, {
          deserializeOutput: false,
        })
        .then((res) => {
          return camelCaseKeys(res.api_response) as
            | TrackingResponse
            | TrackingResponseError;
        }),
  }),
});

export const useOrderVariantsQuery = (
  params: Record<string, any>,
  options?: QueryOptions<OrderVariant[]>
) => {
  return useQuery({
    ...orderVariantsKeyFactory.list(params),
    ...options,
  });
};

export const usePaginatedOrderVariantsQuery = buildPaginatedQuery<OrderVariant>(
  orderVariantsKeyFactory.paginated,
  {
    placeholderData: keepPreviousData,
  }
);

export const useOrderVariantTrackingQuery = (orderVariantId: string) => {
  return useQuery({
    ...orderVariantsKeyFactory.track(orderVariantId),
    staleTime: 1000 * 60 * 10,
  });
};

export const useCancelOrderVariantsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderVariantIds,
      note,
      cancelationType = "order",
    }: {
      orderVariantIds: (string | number)[];
      note: string;
      cancelationType?: string;
    }) => {
      for (const orderVariantId of orderVariantIds) {
        await client.post(
          `order-variants/${orderVariantId}/cancel`,
          {
            "cancelation-type": cancelationType,
            "cancelation-note": note,
          },
          { serializeBody: false }
        );
      }
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: orderVariantsKeyFactory.list._def,
        }),
        queryClient.invalidateQueries({
          queryKey: orderVariantsKeyFactory.paginated._def,
        }),
        queryClient.invalidateQueries({
          queryKey: itemRollupsKeyFactory.paginated._def,
        }),
      ]),
  });
};

export const useMarkAsTransferredMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderVariantIds: string[]) =>
      client.post(
        "order-variants/mark-as-transferred",
        { ov_ids: orderVariantIds },
        { serializeBody: false }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orderVariantsKeyFactory.list._def,
      });
      queryClient.invalidateQueries({
        queryKey: orderVariantsKeyFactory.paginated._def,
      });
      return queryClient.invalidateQueries({
        queryKey: itemRollupsKeyFactory.paginated._def,
      });
    },
  });
};
